import { TFunction } from 'i18next';
import { formatPlatformName } from '../../../services/helperService';
import { formatNormalizedUnitPrice, StdUnit } from '../../../services/inventory/inventoryService';
import { getTableFormattedDate } from '../../../utils/dateUtils';
import { VarianceRow, VariancePoolMember } from '../../../utils/reports/inventoryVariance';
import { LeakageQtyFormat } from './leakagePoolDisplay';

/**
 * Client-side CSV export of the Leakage tab's product table.
 *
 * The rows handed in are exactly the rows the table is showing — every filter (direction
 * tab, favourites, search, confidence, category) and the sort are already applied by the
 * caller, and the numbers come from the report computed for the selected inventory,
 * count and "compared against" baseline. The export never re-derives or re-filters.
 *
 * Per row it carries the table's columns first, then the facts the detail drawer adds
 * (supplier, article code, valuation price, sections, the € behind each quantity, dishes)
 * — the same idea as the product-utilization export, which folds its drawer's dish list
 * into the file.
 *
 * A pooled row is followed by one indented line per member carrying that member's FACTS
 * only: expected and leakage stay on the pool row, because a member's share of them is an
 * allocation estimate, not an observation (the table's rule).
 */

/** One product row plus the context only the tab can resolve. */
export interface LeakageCsvEntry {
  row: VarianceRow & { groupName?: string | null };
  /** Supplier behind the product (a pool answers through its primary member). */
  supplierName: string | null;
  /** Article code of the linked order product, when one resolved. */
  articleId: string | null;
  /** Last order carrying the product. */
  lastOrderDate: Date | null;
  /** Reconciled period of this row's group, e.g. "12 Jan → 19 Jan". */
  periodLabel: string;
}

export const escapeCell = (value: string | number): string => {
  const s = String(value ?? '');
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/** Table dashes become empty cells so spreadsheets keep the columns numeric. */
export const noDash = (text: string): string => (text === '—' ? '' : text);

export const money = (v: number | null | undefined): string => (v === null || v === undefined ? '' : v.toFixed(2));

/** Data-quality flags + pool caveats, exactly what the table's Flags / Status cells badge. */
function flagsText(t: TFunction, row: VarianceRow): string {
  const parts: string[] = [];
  if (row.moved) parts.push(t('inventory.leakage.flag.moved'));
  if (row.alsoInGroups.length > 0) parts.push(t('inventory.leakage.flag.alsoIn', { groups: row.alsoInGroups.join(', ') }));
  if (row.hasUnmeasurablePurchases) parts.push(t('inventory.leakage.flag.unmeasurable'));
  if (row.countUnitMismatch) parts.push(t('inventory.leakage.flag.countUnit'));
  if (row.neededUnitMismatch) parts.push(t('inventory.leakage.flag.recipeUnit'));
  if (row.noRecipeCoverage) parts.push(t('inventory.leakage.flag.noRecipe'));
  const uncounted = row.pool?.membersUncounted ?? [];
  const excluded = row.pool?.membersExcluded ?? [];
  const elsewhere = row.pool?.membersInOtherGroups ?? [];
  if (uncounted.length > 0)
    parts.push(`${t('inventory.leakage.pool.uncounted', { count: uncounted.length })}: ${uncounted.join(', ')}`);
  if (excluded.length > 0)
    parts.push(`${t('inventory.leakage.pool.excluded', { count: excluded.length })}: ${excluded.join(', ')}`);
  if (elsewhere.length > 0)
    parts.push(`${t('inventory.leakage.pool.otherGroups', { count: elsewhere.length })}: ${elsewhere.join(', ')}`);
  return parts.join(' | ');
}

/** Member badges as text: primary/alternative, plus "not counted" / "inert" when it applies. */
function memberFlagsText(t: TFunction, m: VariancePoolMember): string {
  const parts = [t(m.isPrimary ? 'inventory.leakage.pool.primary' : 'inventory.leakage.pool.alternative')];
  if (!m.counted) parts.push(t(m.inert ? 'inventory.leakage.pool.inert' : 'inventory.leakage.pool.notCounted'));
  return parts.join(' | ');
}

/**
 * €/std-unit the report valued the row at, normalised the way the drawer shows it (g→kg,
 * ml→L). A pool answers through its primary member, whose own price chain priced its share.
 */
function valuationPriceText(row: VarianceRow): string {
  const primary = row.members?.find((m) => m.isPrimary);
  const stdUnit = primary ? primary.stdUnit : row.stdUnit;
  const pricePerStdUnit = primary ? primary.pricePerStdUnit : row.pricePerStdUnit;
  const price = formatNormalizedUnitPrice({
    stdUnit: stdUnit as StdUnit,
    pricePerStdUnit,
    // Only read for the 'unit' std unit, where €/std-unit IS the per-unit price.
    latestUnitPrice: pricePerStdUnit ?? 0,
  });
  return price ? `${price.value.toFixed(2)} / ${price.unit}` : '';
}

/**
 * A quantity column's header, tagged with the active unit mode when it is not the default.
 * The cells already print their unit inline, but that unit differs per product in count
 * mode — so the file says once, up top, which view produced it.
 */
export function qtyHeader(t: TFunction, format: LeakageQtyFormat, label: string): string {
  return format.mode === 'count' ? `${label} (${t('inventory.leakage.unitModeCount')})` : label;
}

export function leakageCsvHeaders(t: TFunction, includeGroup: boolean, format: LeakageQtyFormat): string[] {
  const q = (label: string): string => qtyHeader(t, format, label);
  return [
    t('inventory.product'),
    ...(includeGroup ? [t('inventory.exportGroup')] : []),
    t('inventory.leakage.csv.period'),
    t('inventory.leakage.colSection'),
    q(t('inventory.leakage.colExpectedQty')),
    q(t('inventory.leakage.colCountedQty')),
    q(t('inventory.leakage.colLeakageQty')),
    t('inventory.leakage.colLeakageEur'),
    q(t('inventory.leakage.colOpening')),
    q(t('inventory.leakage.colPurchased')),
    q(t('inventory.leakage.colUsage')),
    t('inventory.colProductCategory'),
    t('inventory.leakage.colConfidence'),
    t('inventory.colStatus'),
    t('inventory.leakage.colFlags'),
    t('inventory.colSupplier'),
    t('reports.utilization.csv.articleId'),
    t('inventory.leakageDrawer.valuationPrice'),
    t('inventory.leakage.csv.lastOrdered'),
    t('inventory.leakage.csv.sections'),
    t('inventory.leakage.csv.openingValue'),
    t('inventory.leakage.csv.purchasedValue'),
    t('inventory.leakage.csv.usageValue'),
    t('inventory.leakage.csv.expectedValue'),
    t('inventory.leakage.csv.countedValue'),
    t('reports.utilization.col.dishes'),
    t('inventory.leakage.csv.onlyFromSubrecipe'),
  ];
}

/**
 * Base position of the columns the DETAILED export writes into, in the no-group layout.
 * Kept right under `leakageCsvHeaders` so a reordered header can't silently send a section's
 * opening quantity into, say, the leakage column.
 */
const COL_BASE_INDEX = { product: 0, countedQty: 4, openingQty: 7 } as const;

/** Index of one of those columns for the layout actually being written. */
export function leakageCsvColIndex(col: keyof typeof COL_BASE_INDEX, includeGroup: boolean): number {
  const base = COL_BASE_INDEX[col];
  // The product name leads; every later column shifts right when Group is present.
  return base === 0 ? 0 : base + (includeGroup ? 1 : 0);
}

export function rowCells(
  t: TFunction,
  entry: LeakageCsvEntry,
  includeGroup: boolean,
  format: LeakageQtyFormat
): string[] {
  const { row } = entry;
  return [
    row.productName,
    ...(includeGroup ? [row.groupName ?? ''] : []),
    entry.periodLabel,
    row.mixedSections ? t('inventory.leakage.mixed') : row.attributedSectionName ?? '',
    noDash(format.row(row, row.expectedQty)),
    noDash(format.row(row, row.endQty)),
    noDash(format.row(row, row.varianceQty)),
    money(row.varianceValue),
    noDash(format.row(row, row.startQty)),
    noDash(format.row(row, row.purchasedQty)),
    noDash(format.row(row, row.neededQty)),
    row.inventoryCategoryName ?? '',
    t(`inventory.leakage.confidence.${row.confidence}`),
    t(`inventory.leakage.status.${row.status}`),
    flagsText(t, row),
    entry.supplierName ?? '',
    entry.articleId ?? '',
    valuationPriceText(row),
    entry.lastOrderDate ? getTableFormattedDate(entry.lastOrderDate) : '',
    row.sectionChanges
      .map((s) => `${s.sectionName}${s.deleted ? ` ${t('inventory.leakage.deletedSection')}` : ''}`)
      .join(' | '),
    money(row.startValue),
    money(row.purchasedValue),
    money(row.neededValue),
    money(row.expectedValue),
    money(row.countedValue),
    row.dishes.join(' | '),
    // Blank rather than "no": the column only ever calls out the rows whose stock was
    // never counted loose, so a reader scanning it sees exactly those.
    row.onlyFromSubrecipe ? t('inventory.leakage.flag.onlyFromSubrecipe') : '',
  ];
}

export function memberCells(
  t: TFunction,
  m: VariancePoolMember,
  includeGroup: boolean,
  format: LeakageQtyFormat
): string[] {
  // '' when the recipe row carried no platform — same reason the table skips its logo.
  const supplier = m.unknownSupplierName ?? formatPlatformName(m.platform);
  return [
    `  - ${m.productName}`,
    ...(includeGroup ? [''] : []),
    '', // period — the pool row carries it
    '', // section
    '', // expected: an allocation estimate for a member, never shown
    noDash(format.member(m, m.endQty)),
    '', // leakage qty
    '', // leakage €
    noDash(format.member(m, m.startQty)),
    format.member(m, m.purchasedQty),
    // Allocated demand is a supply-proportional split, never an observation — the table
    // marks it with ≈ and so does the file.
    `≈ ${format.member(m, m.allocatedNeededQty)}`,
    '', // product category
    '', // confidence
    '', // status
    memberFlagsText(t, m),
    supplier,
    m.articleId ?? '',
    '', // valuation price
    '', // last ordered
    '', // sections
    money(m.startValue),
    money(m.purchasedValue),
    money(m.allocatedNeededValue),
    '', // expected €
    money(m.countedValue),
    '', // used in dishes
    '', // only from subrecipe — a pool member is always a counted product
  ];
}

export function buildLeakageCsv(
  t: TFunction,
  entries: LeakageCsvEntry[],
  includeGroup: boolean,
  format: LeakageQtyFormat
): string {
  const lines = [leakageCsvHeaders(t, includeGroup, format).map(escapeCell).join(',')];
  for (const entry of entries) {
    lines.push(rowCells(t, entry, includeGroup, format).map(escapeCell).join(','));
    for (const m of entry.row.members ?? []) {
      lines.push(memberCells(t, m, includeGroup, format).map(escapeCell).join(','));
    }
  }
  return lines.join('\n');
}

/** Filename: inventory-leakage[-detailed]-<restaurant-slug>-<yyyy-mm-dd>.csv */
export function leakageCsvFilename(
  restaurantName: string,
  generatedAt: Date,
  kind: 'plain' | 'detailed' = 'plain'
): string {
  const slug = restaurantName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
  const y = generatedAt.getFullYear();
  const m = String(generatedAt.getMonth() + 1).padStart(2, '0');
  const d = String(generatedAt.getDate()).padStart(2, '0');
  return `inventory-leakage${kind === 'detailed' ? '-detailed' : ''}-${slug || 'export'}-${y}-${m}-${d}.csv`;
}
